import React, { useEffect, useState } from 'react';
import { useQueryHelper, useToggleState } from '@anytag/shared-hooks';
import { checkIsReelPost, formatIntNumber } from '@anytag/shared-utils';
import { Carousel, WarningModal } from '@anytag/shared-molecules';
import { Anchor, Button, Icon, Loading, Select, SocialIcon, TextCutter, Textarea } from '@anytag/shared-atoms';
import {
  namedOperations,
  InstagramUGCManagementPostDetailsPayload,
  useRequestInstagramUGCManagementPostMutation
} from '@anytag/graphql';
import { UGCPostStatus } from '../../UGCPostStatus';
import { AnalyticsSelectedAccountInfo } from '../../../../types';
import { useUGCPostOptions, useUpdateUGCPostStatus } from '../../../hooks';

interface StaffTemplateProps {
  analyticsAccount: AnalyticsSelectedAccountInfo;
  data: InstagramUGCManagementPostDetailsPayload;
}

const MAX_COMMENT_LENGTH = 175;

export const StaffTemplate = ({
  analyticsAccount,
  data: { status, postUrl, postSNSId, likes, comments, content, mediaURLs, staffComment, commentingAllowed }
}: StaffTemplateProps) => {
  const confirmModal = useToggleState();
  const { statusOptions } = useUGCPostOptions();
  const { t, enqueueSnackbar } = useQueryHelper();
  const [comment, setComment] = useState<string>(staffComment?.content || '');
  const { handleChangeStatus, updating } = useUpdateUGCPostStatus({ postSNSId, analyticsAccount });
  const { callRequestInstagramUGCManagementPost, loading } = useRequestInstagramUGCManagementPostMutation({
    refetchQueries: [namedOperations.Query.InstagramUGCManagementPostDetails]
  });

  useEffect(() => {
    setComment(staffComment?.content || '');
  }, [staffComment?.content]);

  // TODO: Specs: For the first release, we don’t need to let users log in with different IG accounts. Please set the IG account (linked with Analytics) as default.
  const isLoggedIn = true;

  const handleCommentInputChange = ({ target: { value } }: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (value.length > MAX_COMMENT_LENGTH) {
      return;
    }

    setComment(value);
  };

  const handlePostComment = async () => {
    try {
      await callRequestInstagramUGCManagementPost({
        variables: {
          input: {
            content: comment,
            postSnsId: postSNSId,
            analyticsAccountId: analyticsAccount.id
          }
        }
      });
      enqueueSnackbar(t('succeededInSave'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    } finally {
      confirmModal.close();
    }
  };

  return (
    <>
      {updating ? <Loading className="absolute top-0 left-0 z-5 m-0 size-full bg-white/40" /> : null}

      <div className="box-border max-h-[calc(100vh-3rem)] overflow-auto">
        <div className="flex items-center">
          <p className="text-xl font-semibold">{t('Request Rights')}</p>
        </div>

        <div className="mt-4 flex gap-6">
          <div className="box-border flex-1 text-base">
            <p>{t('UGC.Request description')}</p>

            <div className="mt-4 flex items-center justify-between rounded-sm border-base-border bg-black-90 p-2">
              <p className="font-semibold text-white">{t('Authorization status')}</p>
              <Select
                value={status}
                className="w-36"
                clearable={false}
                disabled={updating}
                options={statusOptions}
                onChange={handleChangeStatus}
              />
            </div>

            <div className="mt-4 rounded-sm border-base-border">
              <div className="flex items-center px-4 py-2.5">
                <Icon size="24px" icon="sending-mail" />
                <p className="ml-2 font-semibold">{t('Ask permission by Instagram DM')}</p>
              </div>

              <div className="border-t-border p-4">
                <p>{t('Ask creator permission by your Instagram DM')}</p>
                <Anchor
                  variant="blue"
                  href={postUrl}
                  target="_blank"
                  className="mt-3 flex items-center text-base leading-none whitespace-nowrap"
                >
                  {t('Contact via Instagram')}
                  <Icon icon="external-link" size="16px" variant="blue" className="ml-1.5" />
                </Anchor>
              </div>
            </div>

            <div className="mt-4 rounded-sm border-base-border">
              <div className="flex items-center px-4 py-3.25">
                <Icon size="20px" icon="comment" variant="gray" />
                <p className="ml-2 font-semibold">{t('Add comment on post to notify your DM')}</p>
              </div>

              <div className="border-t-border p-4">
                <div className="flex items-start">
                  <div className="flex-1/2">
                    <p>{t('Post as')}&nbsp;:</p>
                    <p className="max-w-32.5 text-sm leading-4 text-black-60">({t('connect your business account')})</p>
                  </div>

                  {isLoggedIn ? (
                    <div className="mt-1.5 flex flex-1/2 items-center">
                      <SocialIcon size="16px" className="mr-1" type={analyticsAccount.socialAccountType} />
                      <TextCutter text={analyticsAccount.socialAccountName} className="font-semibold" />
                      <Anchor
                        className="flex items-center text-base leading-none whitespace-nowrap"
                        target="_blank"
                        variant="blue"
                        href={analyticsAccount.socialMediaUrl}
                      >
                        <Icon icon="external-link" size="16px" variant="blue" className="ml-1.5" />
                      </Anchor>
                    </div>
                  ) : (
                    <Button variant="outline" title={t('Log in with brand Account')} />
                  )}
                </div>

                <div className="mt-2 flex">
                  <p className="flex-1/2">{t('Comment date')}&nbsp;:</p>
                  <p className="flex-1/2">
                    {isLoggedIn && !!staffComment?.date
                      ? new Date(staffComment.date).toISOString().split('T')[0] || '-'
                      : '---'}
                  </p>
                </div>

                <p className="mt-6">{t('Add comment on this post ')}</p>
                <Textarea
                  value={comment}
                  className="mt-1 h-30"
                  disabled={!commentingAllowed}
                  onChange={handleCommentInputChange}
                />

                <div className="mt-4 flex items-center">
                  <p className="text-black-60">
                    {comment.length}/{MAX_COMMENT_LENGTH}
                  </p>
                  <Button
                    variant="outline"
                    title={t('Post comment')}
                    onClick={confirmModal.open}
                    disabled={!isLoggedIn || !commentingAllowed || !comment}
                    className="ml-auto px-4"
                    tooltip={{
                      withinPortal: false,
                      disabled: isLoggedIn,
                      help: t<string>('Please log in with the account you want to post')
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="box-border flex w-91 flex-none flex-col text-base">
            <div className="relative">
              <Carousel
                showIcon
                className="size-91"
                isReel={checkIsReelPost(postUrl)}
                sources={mediaURLs.length > 0 ? mediaURLs : ['']}
              />
              <UGCPostStatus status={status} />
            </div>

            <div className="box-border flex flex-[1_1_auto] flex-col rounded-b-sm border-base-border border-t-[none] p-4">
              <div className="flex items-start">
                <SocialIcon size="16px" className="mr-4.5" type={analyticsAccount.socialAccountType} />
                <p className="box-border px-2">
                  <span className="text-base leading-4">{formatIntNumber(likes)}</span>
                  <br />
                  <span className="text-xs leading-3.25 text-black-60">{t('Likes')}</span>
                </p>
                <p className="box-border border-l-border px-2">
                  <span className="text-base leading-4">{formatIntNumber(comments)}</span>
                  <br />
                  <span className="text-xs leading-3.25 text-black-60">{t('Comments')}</span>
                </p>
              </div>

              <p className="mt-4 text-sm font-semibold">{t('Post’s Caption')}</p>

              <div className="mt-2 flex-1 border-base-border p-2">
                <TextCutter lines={6} text={content} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <WarningModal
        open={confirmModal.status}
        onClose={confirmModal.close}
        title={t('Are you sure to publish this comment?')}
        footer={{
          cancelButton: { title: t('Button.Cancel'), onClick: confirmModal.close },
          submitButton: { loading, title: t('Button.OK'), onClick: handlePostComment }
        }}
      />
    </>
  );
};
